import { Controller } from "@hotwired/stimulus";
import { AsYouType, parsePhoneNumber } from "libphonenumber-js";

export default class extends Controller {
  static targets = [
    "input",
    "countrySelect",
    "hiddenInput",
    "countryCodeDisplay"
  ];

  connect() {
    this.updateCountryCodeDisplay();
    // Initialize with empty national number on first load
    if (this.hasInputTarget && !this.inputTarget.value) {
      this.inputTarget.value = "";
    } else if (this.hasInputTarget && this.inputTarget.value) {
      // If there's an existing value, parse it and update everything
      this.parseExistingNumber();
    }
  }

  parseExistingNumber() {
    try {
      // Try to parse the existing full number
      const fullNumber = this.inputTarget.value;
      if (!fullNumber.startsWith("+")) return;

      const phoneNumber = parsePhoneNumber(fullNumber);
      if (phoneNumber) {
        // Set the country dropdown to match the number
        const countryCode = phoneNumber.country;
        if (countryCode && this.hasCountrySelectTarget) {
          // Find the option with this country code
          Array.from(this.countrySelectTarget.options).forEach(option => {
            if (option.value === countryCode) {
              this.countrySelectTarget.value = countryCode;
            }
          });
        }

        // Update the display with just the national part
        this.inputTarget.value = phoneNumber
          .formatNational()
          .replace(/^\+\d+\s/, "");
        this.updateCountryCodeDisplay();
      }
    } catch (error) {
      console.log("Error parsing existing number:", error);
    }
  }

  countryChanged() {
    this.updateCountryCodeDisplay();

    // When country changes, keep only the national part and update the full number
    if (this.hasInputTarget && this.inputTarget.value) {
      const nationalPart = this.cleanNationalNumber(this.inputTarget.value);
      this.inputTarget.value = this.formatNationalWithSpaces(
        nationalPart,
        this.countrySelectTarget.value
      );
      this.updateFullNumber();
    }
  }

  phoneChanged() {
    // Format just the national part
    if (this.hasInputTarget) {
      const nationalPart = this.cleanNationalNumber(this.inputTarget.value);
      this.inputTarget.value = this.formatNationalWithSpaces(
        nationalPart,
        this.countrySelectTarget.value
      );
    }
    this.updateFullNumber();
  }

  cleanNationalNumber(input) {
    // Remove any non-digit characters
    return input.replace(/\D/g, "");
  }

  updateCountryCodeDisplay() {
    if (!this.hasCountrySelectTarget || !this.hasCountryCodeDisplayTarget)
      return;

    const selectedOption = this.countrySelectTarget.options[
      this.countrySelectTarget.selectedIndex
    ];
    const countryCode = selectedOption.getAttribute("data-code");

    this.countryCodeDisplayTarget.textContent = countryCode;
  }

  updateFullNumber() {
    if (
      !this.hasInputTarget ||
      !this.hasCountrySelectTarget ||
      !this.hasHiddenInputTarget
    )
      return;

    const selectedOption = this.countrySelectTarget.options[
      this.countrySelectTarget.selectedIndex
    ];
    const countryCode = selectedOption.getAttribute("data-code");
    const nationalPart = this.cleanNationalNumber(this.inputTarget.value);

    // Build the full international number
    this.hiddenInputTarget.value = `${countryCode}${nationalPart}`;
  }

  formatNationalWithSpaces(number, countryCode) {
    if (!number) return "";

    const formatter = new AsYouType(countryCode);
    const formatted = formatter.input(
      `+${this.getDialCode(countryCode)}${number}`
    );

    // Extract just the national part
    if (formatter.getNumber()) {
      const nationalFormatted = formatter.getNumber().formatNational();
      // Remove country code if it's included
      return nationalFormatted.replace(/^\+\d+\s/, "");
    }

    // Fallback to basic formatting
    switch (countryCode) {
      case "US":
        if (number.length > 3) {
          number = number
            .replace(/(\d{3})(\d{0,3})(\d{0,4})/, "($1) $2-$3")
            .trim();
          return number.replace(/-$/, "");
        }
        return number;
      case "FR":
        return number.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
      default:
        return number.replace(/(\d{3})(?=\d)/g, "$1 ").trim();
    }
  }

  getDialCode(countryCode) {
    const dialCodes = {
      US: "1",
      FR: "33",
      GB: "44",
      DE: "49",
      ES: "34",
      IT: "39"
    };
    return dialCodes[countryCode] || "";
  }
}
